import { usePvgisStore } from './pvgis.store';
import { PvgisStoreState } from './pvgis-state.model';
import { pick } from '@shared/helpers/object.helpers';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { EnvironmentHourData } from '@shared/helpers/energy/weather.models';
import { PvgisWeatherResponse } from '@shared/models/pvgis-weather.model';

/**
 * Get store state without rerendering
 */
export const getPvgisState = usePvgisStore.getState;

// Exposed state keys (excludes internal implementation details)
type ExposedPvgisStateKeys = Exclude<keyof PvgisStoreState, 'currentPromise'>;

/**
 * Hook to select specific properties from PVGIS state
 */
export function usePvgisState<K extends ExposedPvgisStateKeys>(
  ...keys: K[]
): Pick<PvgisStoreState, K> {
  const data = usePvgisStore((state: PvgisStoreState) =>
    keys.length ? pick(state, ...keys) : state,
  );

  return useObjectMemo(data);
}

// Silent version for background fetching (no snackbar notifications)
export const useFetchPvgisData = () => {
  return usePvgisState('fetchPvgisData').fetchPvgisData;
};

/**
 * Hook to get processed environment hour data
 */
export const usePvgisEnvironmentHourData = ():
  EnvironmentHourData[] | undefined => {
  return usePvgisStore((state) => state.weatherData?.environmentHourData);
};

/**
 * Hook to get raw PVGIS weather response
 */
export const usePvgisRawData = (): PvgisWeatherResponse | undefined => {
  return usePvgisStore((state) => state.weatherData?.rawData);
};
